import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { DateTimeRangePicker } from '../../../components/ui/date-time-range-picker';
import { DollarSign, Loader2, Plus, Search, X } from 'lucide-react';
import { api } from '../../../lib/api';
import { formatPrice } from '../../../lib/utils';
import { ExpenseTableRow } from './ExpenseTableRow';
import type { Expense } from '../../../types/car';
import { useLanguage } from '../../../contexts/LanguageContext';

type DateRange = { from: Date | undefined; to?: Date | undefined };
type ExpenseTypeFilter = '' | 'STORE' | 'CAR';
type CurrencyFilter = '' | 'IQD' | 'USD';

interface ExpenseListCardProps {
  expenses: Expense[];
  totalAmount: number;
  totalCount: number;
  totalAmountByCurrency?: Record<string, number>;
  onRefresh: () => void;
  onLoadMore?: () => void;
  isRefreshing?: boolean;
  isLoadingMore?: boolean;
  search: string;
  setSearch: (v: string) => void;
  expenseTypeFilter: ExpenseTypeFilter;
  setExpenseTypeFilter: (v: ExpenseTypeFilter) => void;
  currencyFilter: CurrencyFilter;
  setCurrencyFilter: (v: CurrencyFilter) => void;
  dateRange: DateRange | undefined;
  setDateRange: (v: DateRange | undefined) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

function formatDateTime(dateString: string) {
  const d = new Date(dateString);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function formatAmountByCurrency(amount: number, currency: string): string {
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  }
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount) + ' ' + currency;
}

export function ExpenseListCard({
  expenses,
  totalAmount,
  totalCount,
  totalAmountByCurrency = {},
  onRefresh,
  onLoadMore,
  isRefreshing = false,
  isLoadingMore = false,
  search,
  setSearch,
  expenseTypeFilter,
  setExpenseTypeFilter,
  currencyFilter,
  setCurrencyFilter,
  dateRange,
  setDateRange,
  onClearFilters,
  hasActiveFilters,
}: ExpenseListCardProps) {
  const { t } = useLanguage();
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const currencies = Object.keys(totalAmountByCurrency).filter((c) => (totalAmountByCurrency[c] ?? 0) > 0);
  const multiCurrency = currencies.length > 1;

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/expenses/${id}`);
      setDeleteId(null);
      toast.success(t('expense.deleted'));
      onRefresh();
    } catch (err: unknown) {
      toast.error((err as { error?: string })?.error ?? t('expense.deleteError'));
    }
  };

  return (
    <Card className="rounded-2xl border border-slate-200 shadow-sm">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <DollarSign className="h-5 w-5" />
            {t('expense.list')}
          </CardTitle>
          <CardDescription className="text-slate-500">
            {t('expense.totalCount', { total: formatPrice(totalAmount), count: expenses.length })}
          </CardDescription>
        </div>
        <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg">
          <Link to="/expenses/add-expense">
            <Plus className="h-4 w-4 mr-2" />
            {t('expense.add')}
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Filters */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1fr_1fr_auto_auto] gap-4 items-end">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">{t('expense.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder={t('expense.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 border-slate-200"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">{t('expense.dateRange')}</Label>
              <DateTimeRangePicker value={dateRange} onValueChange={setDateRange} className="w-full" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">{t('expense.type')}</Label>
              <Select
                value={expenseTypeFilter === '' ? '__all__' : expenseTypeFilter}
                onValueChange={(v) => setExpenseTypeFilter(v === '__all__' ? '' : (v as ExpenseTypeFilter))}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue placeholder={t('expense.allTypes')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('expense.allTypes')}</SelectItem>
                  <SelectItem value="STORE">{t('expense.storeLabel')}</SelectItem>
                  <SelectItem value="CAR">{t('expense.carType')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">{t('expense.currency')}</Label>
              <Select
                value={currencyFilter === '' ? '__all__' : currencyFilter}
                onValueChange={(v) => setCurrencyFilter(v === '__all__' ? '' : (v as CurrencyFilter))}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue placeholder={t('expense.allCurrencies')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('expense.allCurrencies')}</SelectItem>
                  <SelectItem value="IQD">IQD</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={onClearFilters}
                className="bg-red-600 hover:bg-red-700"
              >
                <X className="h-4 w-4 mr-2" />
                {t('expense.clearFilters')}
              </Button>
            </div>
          )}
        </div>

        {expenses.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-12 text-center">
            {hasActiveFilters ? (
              <>
                <p className="text-slate-500 mb-4">{t('expense.noExpensesMatch')}</p>
                <Button type="button" variant="destructive" size="sm" onClick={onClearFilters} className="bg-red-600 hover:bg-red-700">
                  <X className="h-4 w-4 mr-2" />
                  {t('expense.clearFilters')}
                </Button>
              </>
            ) : (
              <>
                <p className="text-slate-500 mb-4">{t('expense.noExpensesYet')}</p>
                <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Link to="/expenses/add-expense">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('expense.add')}
                  </Link>
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="relative">
            {isRefreshing && (
              <div className="absolute inset-0 bg-white/60 rounded-lg flex items-center justify-center z-10">
                <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
              </div>
            )}
          <Table aria-label="Expense list">
            <TableCaption className="sr-only">Expense list with ID, date, type, description, category, car, and amount</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className="text-slate-600 font-medium text-sm w-[72px]">{t('expense.id')}</TableHead>
                <TableHead className="text-slate-600 font-medium text-sm">{t('expense.date')}</TableHead>
                <TableHead className="text-slate-600 font-medium text-sm">{t('expense.type')}</TableHead>
                <TableHead className="text-slate-600 font-medium text-sm">{t('expense.descriptionHeader')}</TableHead>
                <TableHead className="text-slate-600 font-medium text-sm">{t('expense.categoryHeader')}</TableHead>
                <TableHead className="text-slate-600 font-medium text-sm">{t('expense.carType')}</TableHead>
                <TableHead className="text-right text-slate-600 font-medium text-sm">{t('expense.amountHeader')}</TableHead>
                <TableHead className="w-[120px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((exp) => (
                <ExpenseTableRow
                  key={exp.id}
                  expense={exp}
                  formatDateTime={formatDateTime}
                  formatPrice={formatPrice}
                  deleteId={deleteId}
                  setDeleteId={setDeleteId}
                  onDeleteSuccess={handleDelete}
                />
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className="bg-slate-200/80 hover:bg-slate-200/80 border-t-2 border-slate-300">
                <TableCell colSpan={6} className="text-sm font-medium text-slate-800">
                  Total: ({expenses.length} expense{expenses.length === 1 ? '' : 's'})
                  {totalCount > expenses.length && ` (showing ${expenses.length} of ${totalCount})`}
                </TableCell>
                <TableCell className="text-right text-sm font-medium tabular-nums">
                  {multiCurrency ? (
                    <>
                      {currencies.map((c, i) => (
                        <span key={c}>
                          {i > 0 && <span className="text-slate-400 mx-1">·</span>}
                          <span className="text-amber-600">{formatAmountByCurrency(totalAmountByCurrency[c] ?? 0, c)}</span>
                        </span>
                      ))}
                    </>
                  ) : currencies.length === 1 ? (
                    <span className="text-green-600">Total amount: {formatAmountByCurrency(totalAmountByCurrency[currencies[0]] ?? totalAmount, currencies[0])}</span>
                  ) : (
                    <span className="text-green-600">Total amount: {formatPrice(totalAmount)}</span>
                  )}
                </TableCell>
                <TableCell className="w-[120px]"></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
          {onLoadMore && expenses.length < totalCount && (
            <div className="flex justify-center pt-4">
              <Button type="button" variant="outline" onClick={onLoadMore} disabled={isLoadingMore} className="border-slate-200">
                {isLoadingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('expense.loadMore')
                )}
              </Button>
            </div>
          )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
